/**
 * CorruptionTheme (S126 / BL-112 / D-224) — the ONE render-side intensity seam
 * for the ECHELON corruption. Every layer (claimed cells, sigil, veil, tears,
 * portal/dash tint, mainframe taint) reads `corruptionIntensity` — no layer
 * does its own wave math. Design contract: docs/design/ACT_III_CORRUPTION.md.
 *
 * Boundaries (must stay true):
 *  - Pure functions. No DOM, no EventBus, no sim access, no `gs.rng`, no clock.
 *  - Act I is a HARD ZERO: `corruptionIntensity(wave ≤ 12)` is 0 regardless of
 *    momentum, and `mixHex(a, b, 0) === a` byte-exact — together these are the
 *    Acts-I-pixel-identical guarantee.
 *  - `EpochTheme.corruption` pins at 1 for all of Act III; the intra-Act-III
 *    build to the wave-35 peak is derived HERE, render-only ((wave−25)/10).
 */

import type { QualityLevel } from "../core/types.ts";
import { GAME } from "../core/config.ts";

/** Structurally identical to the comms `RivalryTone` (ui/CommsMomentum) — kept
 *  as a local union so `rendering` doesn't grow its first ui-layer import; the
 *  wiring site adapts (the unions are assignable). */
export type CorruptionPressure = "ascendant" | "rattled" | "even";

/** Momentum modifier — FENRIR pressing tightens her grip, a dominating player
 *  makes it strain (one threat, two channels; harmonized, never driving). */
const MOMENTUM_SHIFT: Record<CorruptionPressure, number> = {
  ascendant: 0.1,
  rattled: -0.1,
  even: 0,
};

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * S141 / BL-128 / D-236 — the crescendo breather floor. When per-wave PRESSURE
 * is 0 (a breather / the build phase), the corruption ebbs to this fraction of
 * the act's peak; at pressure 1 (a boss/elite/flank spike) it reaches the full
 * act ramp. This is the BL-128 fix: the red is no longer a constant wash — it
 * SPIKES with real threat and BREATHES in the lulls, so it MEANS something.
 */
export const CORRUPTION_BREATHER_FLOOR = 0.45;

/**
 * The one knob, 0..1. Act I → 0 (hard, momentum cannot raise it); Act II creeps
 * 0.04 → 0.5; Act III builds 0.5 → 1.0 at the wave-35 finale and stays pinned
 * there in endless. `tone` is the read-only comms rivalry tone (±0.1).
 *
 * S141 / BL-128: `pressure` (0..1, default 1 → the act PEAK) makes the act ramp
 * the *spike ceiling* and lets the intensity ebb to {@link
 * CORRUPTION_BREATHER_FLOOR} of it in the lulls — a meaningful crescendo, not a
 * constant wash. `pressure = 1` is byte-identical to the pre-S141 ramp (the
 * D-224 contract + every existing test hold).
 */
export function corruptionIntensity(
  wave: number,
  tone: CorruptionPressure = "even",
  pressure = 1,
): number {
  if (wave <= GAME.WAVES_ACT_I) return 0;
  let base: number;
  if (wave <= GAME.WAVES_ACT_II) {
    base = 0.5 * ((wave - GAME.WAVES_ACT_I) / GAME.WAVES_ACT_I);
  } else {
    const actIII = GAME.WAVES_ACT_III - GAME.WAVES_ACT_II - 1; // build span 25→35
    base =
      0.5 + 0.5 * clamp01((wave - (GAME.WAVES_ACT_II + 1)) / Math.max(1, actIII));
  }
  // Crescendo: scale the act ramp between the breather floor and its full peak.
  const lifted =
    base *
    (CORRUPTION_BREATHER_FLOOR +
      (1 - CORRUPTION_BREATHER_FLOOR) * clamp01(pressure));
  return clamp01(lifted + MOMENTUM_SHIFT[tone]);
}

/** Act III = Dominion: tears + veil + structural mainframe taint arm here. */
export function isDominion(wave: number): boolean {
  return wave > GAME.WAVES_ACT_II;
}

/** Per-quality layer gates + density — the degrade ladder of the Bible §7.
 *  `off` keeps the static veil floor so the act still *reads*. */
export interface CorruptionQualityGates {
  /** Claimed-cell density multiplier (0..1). */
  cellDensity: number;
  /** Signal tears (Act III violence) — high only. */
  tears: boolean;
  /** The surfacing sigil. */
  sigil: boolean;
  /** Veil breathing (false = static veil). */
  veilBreath: boolean;
}

export function corruptionQualityGates(
  quality: QualityLevel,
): CorruptionQualityGates {
  switch (quality) {
    case "high":
      return { cellDensity: 1, tears: true, sigil: true, veilBreath: true };
    case "medium":
      return { cellDensity: 0.6, tears: false, sigil: true, veilBreath: true };
    case "low":
      return { cellDensity: 0.4, tears: false, sigil: false, veilBreath: false };
    default:
      return { cellDensity: 0, tears: false, sigil: false, veilBreath: false };
  }
}

const HEX_CACHE = new Map<string, [number, number, number]>();

function parseHex(hex: string): [number, number, number] {
  let rgb = HEX_CACHE.get(hex);
  if (!rgb) {
    rgb = [
      parseInt(hex.slice(1, 3), 16),
      parseInt(hex.slice(3, 5), 16),
      parseInt(hex.slice(5, 7), 16),
    ];
    HEX_CACHE.set(hex, rgb);
  }
  return rgb;
}

/**
 * Mix two `#rrggbb` colors. EXACT contract: `t <= 0` returns `a` byte-identical
 * (the Acts-I guarantee — an untinted style string never changes), `t >= 1`
 * returns `b`. Output is `rgb(...)` (computed, not a new hex literal).
 */
export function mixHex(a: string, b: string, t: number): string {
  if (t <= 0) return a;
  if (t >= 1) return b;
  const ca = parseHex(a);
  const cb = parseHex(b);
  const r = Math.round(ca[0] + (cb[0] - ca[0]) * t);
  const g = Math.round(ca[1] + (cb[1] - ca[1]) * t);
  const bl = Math.round(ca[2] + (cb[2] - ca[2]) * t);
  return `rgb(${r},${g},${bl})`;
}

/** mulberry32 — the render-local PRNG (NEVER `gs.rng`; seeded constant so two
 *  layers given the same clock draw the same sequence — testable determinism). */
export function createRenderPrng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Deterministic per-cell hash (the CR-32 grammar): stable for a given cell +
 *  time bucket, no state, no allocation. Returns 0..1. */
export function cellHash(col: number, row: number, bucket: number): number {
  let h = (col * 374761393 + row * 668265263 + bucket * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
